/* eslint-disable no-plusplus */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-continue */

// Standard valence for common elements
const VALENCE_MAP = {
  C: 4,
  N: [3, 5], // N can have 3 or 5
  O: 2,
  S: [2, 4, 6],
  P: [3, 5],
  Cl: 1,
  Br: 1,
  I: 1,
  F: 1,
};

function normalizeElement(rawElem) {
  if (!rawElem || typeof rawElem !== 'string') return '';
  return rawElem[0].toUpperCase() + (rawElem.length > 1 ? rawElem.slice(1).toLowerCase() : '');
}

function getValence(elem) {
  const val = VALENCE_MAP[elem];
  if (Array.isArray(val)) return val[0]; // Default to first valence if multiple
  return val || 0;
}

function buildHillFormula(elementCounts) {
  if (!elementCounts || typeof elementCounts !== 'object') return '';

  const counts = { ...elementCounts };
  const parts = [];

  const addElement = (symbol) => {
    const count = counts[symbol];
    if (count) {
      parts.push(symbol + (count > 1 ? count : ''));
      delete counts[symbol];
    }
  };

  addElement('C');
  addElement('H');

  Object.keys(counts)
    .sort()
    .forEach((el) => {
      parts.push(el + (counts[el] > 1 ? counts[el] : ''));
    });

  return parts.join('');
}

// ── V3000 ──────────────────────────────────────────────────────────────────
function countElementsV3000(lines) {
  const elementCounts = Object.create(null);
  const atomMap = Object.create(null); // Map atomId → element
  const atomDegrees = Object.create(null); // Map atomId → bond count

  const atomLineRe = /^M\s+V30\s+(\d+)\s+([A-Za-z][a-z]?)\b/;

  let inAtomBlock = false;
  let inBondBlock = false;

  for (const rawLine of lines) {
    const line = rawLine.trim();

    // Handle atom block
    if (!inAtomBlock && /^M\s+V30\s+BEGIN\s+ATOM/i.test(line)) {
      inAtomBlock = true;
      inBondBlock = false;
      continue;
    }

    if (inAtomBlock && /^M\s+V30\s+END\s+ATOM/i.test(line)) {
      inAtomBlock = false;
      continue;
    }

    if (inAtomBlock) {
      const m = atomLineRe.exec(line);
      if (m) {
        const atomId = m[1];
        const elem = normalizeElement(m[2]);
        if (elem) {
          atomMap[atomId] = elem;
          elementCounts[elem] = (elementCounts[elem] || 0) + 1;
          atomDegrees[atomId] = 0; // Initialize degree
        }
      }
      continue;
    }

    // Handle bond block
    if (!inBondBlock && /^M\s+V30\s+BEGIN\s+BOND/i.test(line)) {
      inBondBlock = true;
      continue;
    }

    if (inBondBlock && /^M\s+V30\s+END\s+BOND/i.test(line)) {
      inBondBlock = false;
      continue;
    }

    if (inBondBlock) {
      // Bond line format: M  V30 <id> <order> <from> <to>
      const parts = line.split(/\s+/);
      if (parts.length >= 6 && parts[0] === 'M' && parts[1] === 'V30') {
        const bondOrderCode = parseInt(parts[3], 10); // 1=single, 2=double, 3=triple, 4=aromatic
        const bondOrder = bondOrderCode === 4 ? 1.5 : bondOrderCode;
        const fromId = parts[4];
        const toId = parts[5];

        if (!isNaN(bondOrder) && atomDegrees[fromId] !== undefined && atomDegrees[toId] !== undefined) {
          atomDegrees[fromId] += bondOrder;
          atomDegrees[toId] += bondOrder;
        }
      }
    }
  }

  // Calculate and add implicit hydrogens
  for (const atomId of Object.keys(atomMap)) {
    const elem = atomMap[atomId];
    const degree = atomDegrees[atomId] || 0;
    const valence = getValence(elem);
    const implicitH = Math.max(0, valence - degree);

    if (implicitH > 0) {
      elementCounts.H = (elementCounts.H || 0) + implicitH;
    }
  }

  return elementCounts;
}

// ── V2000 ──────────────────────────────────────────────────────────────────
// V2000 format:
//   Line 4 (index 3): counts line — first 3 chars = atom count, next 3 = bond count, ends with "V2000"
//   Atom block starts at line 5 (index 4), each atom line has element symbol at columns 31–34
function isValidElement(elem) {
  const pseudoAtoms = new Set(['R', 'X', 'A', 'Q']);
  return elem && /^[A-Z][a-z]?$/.test(elem) && !pseudoAtoms.has(elem);
}

function countElementsV2000(lines) {
  const elementCounts = Object.create(null);
  const atomMap = Object.create(null); // Map atomIndex → element
  const atomDegrees = Object.create(null); // Map atomIndex → bond count

  // Counts line is index 3; atom count is the first integer on that line
  const countsLine = lines[3] || '';
  const countsMatch = countsLine.match(/^\s*(\d+)\s+(\d+)/);
  if (!countsMatch) return elementCounts;

  const atomCount = parseInt(countsMatch[1], 10);
  const bondCount = parseInt(countsMatch[2], 10);
  if (!Number.isFinite(atomCount) || atomCount <= 0) return elementCounts;

  // Atom block starts at line index 4
  for (let i = 4; i < 4 + atomCount; i++) {
    const line = lines[i];
    if (!line) break;

    // Element symbol occupies columns 31–34 (0-indexed) in the fixed-width format
    const m = line.match(/^\s*[-\d.]+\s+[-\d.]+\s+[-\d.]+\s+([A-Za-z][a-z]?)\b/);
    if (m) {
      const elem = normalizeElement(m[1]);
      if (isValidElement(elem)) {
        atomMap[i] = elem;
        elementCounts[elem] = (elementCounts[elem] || 0) + 1;
        atomDegrees[i] = 0; // Initialize degree
      }
    }
  }

  // Bond block starts after atoms; read bond count
  const bondStart = 4 + atomCount;
  for (let i = bondStart; i < bondStart + bondCount; i++) {
    const line = lines[i];
    if (!line) break;

    // V2000 bond line format: from(3) to(3) order(3) stereo(3)
    const m = line.match(/^\s*(\d+)\s+(\d+)\s+(\d+)/);
    if (m) {
      const fromIdx = parseInt(m[1], 10) - 1; // 1-indexed → 0-indexed
      const toIdx = parseInt(m[2], 10) - 1;
      const bondOrder = parseInt(m[3], 10);

      const fromAtomIdx = 4 + fromIdx;
      const toAtomIdx = 4 + toIdx;

      if (atomDegrees[fromAtomIdx] !== undefined && atomDegrees[toAtomIdx] !== undefined) {
        atomDegrees[fromAtomIdx] += bondOrder;
        atomDegrees[toAtomIdx] += bondOrder;
      }
    }
  }

  // Calculate and add implicit hydrogens
  for (const atomIndex of Object.keys(atomMap)) {
    const elem = atomMap[atomIndex];
    const degree = atomDegrees[atomIndex] || 0;
    const valence = getValence(elem);
    const implicitH = Math.max(0, valence - degree);

    if (implicitH > 0) {
      elementCounts.H = (elementCounts.H || 0) + implicitH;
    }
  }

  return elementCounts;
}

// ── Public API ─────────────────────────────────────────────────────────────
function parseV3000SumFormula(molfile) {
  if (!molfile || typeof molfile !== 'string' || !molfile.trim()) return '';

  const lines = molfile.split(/\r?\n/);

  // Detect version from the counts line (line index 3) or anywhere in the file
  const isV3000 = lines.some((l) => /V3000/i.test(l));
  const isV2000 = !isV3000 && lines.some((l) => /V2000/i.test(l));

  let elementCounts;
  if (isV3000) {
    elementCounts = countElementsV3000(lines);
  } else if (isV2000) {
    elementCounts = countElementsV2000(lines);
  } else {
    // Unknown / unsupported format — attempt V3000 parse as fallback
    elementCounts = countElementsV3000(lines);
  }

  return buildHillFormula(elementCounts);
}

export default parseV3000SumFormula;
